import React from 'react';

export const getRoomFromURL = (): string | undefined => getFromURL('room');

export const getFromURL = (
    key: string,
    search: string = window.location.search
): string | undefined =>
    search
        .slice(1)
        .split('&')
        .find((param) => param.startsWith(`${key}=`))
        ?.split('=')[1];

export type UseRoomIDOptions = {
    /**
     * When false (receiver): never read/write `?room=` — id lives only in memory so each full
     * refresh can mint a new room without reusing a bookmarked query string.
     */
    persistInUrl?: boolean;
};

function stripRoomQueryParam(): void {
    if (!getRoomFromURL()) return;
    const params = new URLSearchParams(window.location.search);
    params.delete('room');
    const next = params.toString();
    const path = window.location.pathname;
    window.history.replaceState(window.history.state, '', next ? `${path}?${next}` : path);
}

export const useRoomID = (
    options: UseRoomIDOptions = {}
): [string | undefined, (v?: string) => void] => {
    const persistInUrl = options.persistInUrl !== false;

    const [state, setState] = React.useState<string | undefined>(() =>
        persistInUrl ? getRoomFromURL() : undefined
    );

    React.useEffect(() => {
        if (!persistInUrl) {
            stripRoomQueryParam();
        }
    }, [persistInUrl]);

    React.useEffect(() => {
        if (!persistInUrl) return;
        const onChange = (): void => setState(getRoomFromURL());
        window.addEventListener('popstate', onChange);
        return () => window.removeEventListener('popstate', onChange);
    }, [persistInUrl]);

    const setRoomID = React.useCallback(
        (id?: string) =>
            setState((oldId?: string) => {
                if (oldId !== id) {
                    if (persistInUrl) {
                        window.history.pushState({roomId: id}, '', id ? `?room=${id}` : '?');
                    }
                }
                return id;
            }),
        [persistInUrl]
    );

    return [state, setRoomID];
};
